import React, { useEffect, useState } from 'react';
import { cn, Modal } from '@/common/ui/Core';
import { CloseIcon } from '@/assets/icons';

interface SlidingCurtainProps {
    isOpen: boolean;
    onClose: () => void;
    title: string;
    subtitle?: string;
    icon?: React.ReactNode;
    description: string;
    children?: React.ReactNode;
    /* Actions to show at the bottom (e.g. Dismiss, Understood) */
    actions?: {
        label: string;
        onClick: () => void;
        variant?: 'primary' | 'secondary';
    }[];
}

export const SlidingCurtain: React.FC<SlidingCurtainProps> = ({
    isOpen,
    onClose,
    title,
    subtitle,
    icon,
    description,
    children,
    actions
}) => {
    const [isMobile, setIsMobile] = useState(false);
    const [isVisible, setIsVisible] = useState(false);

    useEffect(() => {
        const checkMobile = () => {
            setIsMobile(window.innerWidth < 1040);
        };
        checkMobile();
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, []);

    useEffect(() => {
        if (isOpen) {
            // Tiny delay to ensure the browser paints the initial "off-screen" position
            const timer = setTimeout(() => setIsVisible(true), 10);
            return () => clearTimeout(timer);
        } else {
            setIsVisible(false);
        }
    }, [isOpen]);

    // Mobile Fallback: Use existing Modal component
    if (isMobile) {
        return (
            <Modal
                isOpen={isOpen}
                onClose={onClose}
                title={title}
                variant="premium"
                className="max-w-[400px]"
            >
                <div className="p-8 text-center flex flex-col items-center">
                    {icon && (
                        <div className="w-16 h-16 rounded-3xl bg-primaryGreen/5 flex items-center justify-center mb-6">
                            {icon}
                        </div>
                    )}
                    {subtitle && <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2">{subtitle}</p>}
                    <p className="text-sm text-gray-500 leading-relaxed mb-8">{description}</p>
                    {children}

                    {actions && (
                        <div className="flex flex-col gap-3 w-full mt-4">
                            {actions.map((action, i) => (
                                <button
                                    key={i}
                                    onClick={action.onClick}
                                    className={cn(
                                        "w-full py-3.5 rounded-2xl text-xs font-bold uppercase tracking-widest transition-all",
                                        action.variant === 'primary'
                                            ? "bg-primaryGreen text-white shadow-xl shadow-primaryGreen/20 hover:scale-[1.02]"
                                            : "text-gray-400 hover:text-darkGreen"
                                    )}
                                >
                                    {action.label}
                                </button>
                            ))}
                        </div>
                    )}
                </div>
            </Modal>
        );
    }

    // Desktop/Curtain: Slides from Right
    return (
        <div className={cn("fixed inset-0 z-[140]", !isOpen && !isVisible && "pointer-events-none")}>
            {/* Backdrop */}
            <div
                className={cn(
                    "absolute inset-0 bg-black/60 backdrop-blur-[1px] transition-opacity duration-500",
                    isOpen && isVisible ? "opacity-100" : "opacity-0"
                )}
                onClick={onClose}
            />

            {/* Curtain */}
            <div className={cn(
                "absolute top-0 right-0 h-full w-full bg-white shadow-[-20px_0_50px_var(--app-effect0x0x0a0_05)] transition-transform duration-700 cubic-bezier(0.4, 0, 0.2, 1) flex flex-col sm:w-[25%] sm:min-w-[350px]",
                isOpen && isVisible ? "translate-x-0" : "translate-x-full"
            )}>
                {/* Header */}
                <div className="flex items-center justify-between gap-4 border-b border-gray-50 p-5 sm:p-8">
                    <div className="min-w-0">
                        <h2 className="text-xl font-black text-darkGreen uppercase tracking-tight font-outfit">{title}</h2>
                        {subtitle && <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest mt-1">{subtitle}</p>}
                    </div>
                    <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors text-gray-300 hover:text-darkGreen">
                        <CloseIcon className="w-6 h-6" />
                    </button>
                </div>

                {/* Content */}
                <div className="flex flex-1 flex-col items-center overflow-y-auto p-6 text-center sm:p-10">
                    {icon && (
                        <div className="w-24 h-24 rounded-[2.5rem] bg-gray-50 flex items-center justify-center mb-10 border border-gray-100/50">
                            {icon}
                        </div>
                    )}

                    <div className="max-w-[280px]">
                        <p className="text-base text-gray-500 leading-relaxed font-outfit">
                            {description}
                        </p>
                    </div>

                    <div className="mt-10 w-full">
                        {children}
                    </div>
                </div>

                {/* Footer/Actions */}
                {actions && (
                    <div className="p-8 bg-gray-50/50 border-t border-gray-100 flex flex-col gap-4">
                        {actions.map((action, i) => (
                            <button
                                key={i}
                                onClick={action.onClick}
                                className={cn(
                                    "w-full py-4 rounded-2xl text-xs font-black uppercase tracking-widest transition-all",
                                    action.variant === 'primary'
                                        ? "bg-primaryGreen text-white shadow-2xl shadow-primaryGreen/20 hover:translate-y-[-2px] hover:shadow-primaryGreen/30"
                                        : "text-gray-400 hover:text-darkGreen"
                                )}
                            >
                                {action.label}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};
